import * as THREE from 'three';
import type { Building, Cell } from '../world/city';
import type { Vec } from '../destruction/structure';
import { buildingGeometry } from './geometry';
interface Part {
  signature: string;
  geometry: THREE.BufferGeometry;
}
/** Geometry only: structural connectivity remains across the complete cluster.
 * A changed occupied cell invalidates its section and the six face neighbours.
 */
export class BuildingGeometryCache {
  private parts = new Map<string, Part>();
  rebuild(
    b: Building,
    cells: ReadonlyMap<number, Cell>,
    origin: Vec,
    detail: boolean,
    merge = true,
  ): { geometry: THREE.BufferGeometry; parts: Map<string, THREE.BufferGeometry> } {
    const groups = new Map<string, Map<number, Cell>>(),
      signatures = new Map<string, string>();
    const section = (x: number, y: number, z: number) =>
      `${Math.floor(x / 8)},${Math.floor(y / 4)},${Math.floor(z / 8)}`;
    for (const cell of cells.values()) {
      const id = section(cell.x, cell.y, cell.z);
      let group = groups.get(id);
      if (!group) groups.set(id, (group = new Map()));
      group.set(cell.id, cell);
    }
    for (const [id, group] of groups)
      signatures.set(
        id,
        [...group.values()]
          .map(
            (c) =>
              `${c.id}:${c.hp < 0.6 ? 1 : 0}:${c.fracture ?? 0}:${c.bounds ? Object.values(c.bounds).join(',') : ''}`,
          )
          .join(';'),
      );
    for (const [id, part] of this.parts)
      if (!groups.has(id)) {
        part.geometry.dispose();
        this.parts.delete(id);
      }
    for (const [id, group] of groups) {
      const [x, y, z] = id.split(',').map(Number);
      const neighbours = [
        `${x + 1},${y},${z}`,
        `${x - 1},${y},${z}`,
        `${x},${y + 1},${z}`,
        `${x},${y - 1},${z}`,
        `${x},${y},${z + 1}`,
        `${x},${y},${z - 1}`,
      ];
      const signature = `${detail}:${signatures.get(id)}|${neighbours.map((n) => signatures.get(n) ?? '').join('|')}`;
      const prior = this.parts.get(id);
      if (prior?.signature === signature) continue;
      const context = new Map(group);
      for (const n of neighbours) for (const [cellId, cell] of groups.get(n) ?? []) context.set(cellId, cell);
      prior?.geometry.dispose();
      this.parts.set(id, { signature, geometry: buildingGeometry(b, group, origin, detail, context) });
    }
    const merged = new THREE.BufferGeometry();
    const parts = new Map([...this.parts].map(([id, p]) => [id, p.geometry]));
    // Normal live rendering already consumes these exact sections. A second
    // full-tower concatenation is useful only for a standalone mesh/raycast
    // such as an interior cutaway, not for the batched exterior.
    if (!merge) return { geometry: merged, parts };
    const offsets = [0, 0];
    for (const part of this.parts.values())
      for (const group of part.geometry.groups) offsets[group.materialIndex ?? 0] += group.count;
    const total = offsets[0] + offsets[1];
    for (const attribute of ['position', 'normal', 'color']) {
      const array = new Float32Array(total * 3),
        cursors = [0, offsets[0]];
      for (const part of this.parts.values())
        for (const group of part.geometry.groups) {
          const index = group.materialIndex ?? 0,
            source = part.geometry.getAttribute(attribute).array;
          array.set(source.subarray(group.start * 3, (group.start + group.count) * 3), cursors[index] * 3);
          cursors[index] += group.count;
        }
      merged.setAttribute(attribute, new THREE.BufferAttribute(array, 3));
    }
    merged.addGroup(0, offsets[0], 0);
    if (offsets[1]) merged.addGroup(offsets[0], offsets[1], 1);
    merged.computeBoundingBox();
    merged.computeBoundingSphere();
    return { geometry: merged, parts };
  }
  clear(): void {
    for (const part of this.parts.values()) part.geometry.dispose();
    this.parts.clear();
  }
}
